import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Lottie from 'lottie-react';
import { GenericError, If, ListDropDown } from 'components';
import { useAppSelector } from 'hooks';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { formatAmount } from 'utils/amountUtils';
import { pluckIntValuesFromArrayWithSpecificKey, pluckValuesFromArrayWithSpecificKey } from 'utils/formatters';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { ArrowRightIcon, LineGraphIcon } from 'assets/icons';
import routesPath from 'routes/RoutesPath';
import { jobAmountFilterOptions } from './constants';
import { useLazyGetJobAmountSummaryQuery } from '../api';
import DateUtils from 'utils/dateUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const JobsAmountSummaryWidget: React.FC = () => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const navigate = useNavigate();
  const [getJobAmountSummary, getJobAmountSummaryState] = useLazyGetJobAmountSummaryQuery();
  const [selectedAttribute, setSelectedAttribute] = useState(jobAmountFilterOptions[0]);

  const {
    isFetching: isFetchingJobAmountSummary,
    data: getJobAmountSummaryResponse,
    isSuccess: getJobAmountSummarySuccess,
    isError: isJobAmountSummaryError,
    isUninitialized: isJobAmountSummaryUninitialized
  } = getJobAmountSummaryState;

  const jobCount = getJobAmountSummaryResponse?.data?.count;
  const monthlyCountList = getJobAmountSummaryResponse?.data?.monthWise;

  useEffect(() => {
    if (currentTeam?.id) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      // First date of the current year
      const startOfTheYear = DateUtils.getStartOfYear(currentYear);
      const startDate = DateUtils.startDateToUTCString(
        DateUtils.getDateInFormat({ date: startOfTheYear, dateFormat: 'yyyy-MM-dd' })
      );
      // Last date of the current year
      const endOfTheYear = DateUtils.getEndOfYear(currentYear);
      const endDate = DateUtils.endDateToUTCString(
        DateUtils.getDateInFormat({ date: endOfTheYear, dateFormat: 'yyyy-MM-dd' })
      );
      getJobAmountSummary({ teamId: currentTeam.id, startDate, endDate, attribute: selectedAttribute.value });
    }
  }, [currentTeam, selectedAttribute]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        displayColors: false,
        backgroundColor: '#ECF1F7',
        bodyColor: '#3E556D',
        paddingTop: 5,
        paddingLeft: 9,
        paddingRight: 9,
        paddingBotton: 5,
        titleFont: { weight: 'bold' },
        labelFont: { weight: 'bold' },
        callbacks: {
          label: function (context) {
            return `$${formatAmount(context.parsed.y)}`;
          },
          title: function (context) {
            return null;
          }
        }
      }
    },
    scales: {
      y: {
        display: false,
        grid: {
          display: false
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  const labels = pluckValuesFromArrayWithSpecificKey(monthlyCountList || [], 'month');
  const data = pluckIntValuesFromArrayWithSpecificKey(monthlyCountList || [], 'amount');

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Jobs',
        data: data,
        backgroundColor: '#A4D7FA',
        borderRadius: 4
      }
    ]
  };

  return (
    <div className="w-full pt-5 lg:w-1/2 1439px:pt-10">
      <div className="flex h-[374px] w-full shrink-0 flex-col justify-between rounded border border-borderGray p-5">
        <div className="flex items-baseline justify-start gap-3 text-xl font-bold leading-6 text-primaryText">
          <ListDropDown
            id="job-amount-summary-filter"
            wrapperStyles="w-fit"
            buttonStyles="rounded-[4px] border border-border pl-2 pr-7 py-1 text-[20px] 
            text-primaryText font-bold w-fit"
            optionStyles="min-w-[112px] pb-3"
            itemStyle="text-base leading-5 font-semibold pl-2 pt-3 pr-0 pb-0 text-primaryText"
            items={jobAmountFilterOptions}
            selectedItem={selectedAttribute}
            setSelectedItem={value => setSelectedAttribute(value)}
            iconStyle="right-0"
          />
          this year - {new Date().getFullYear()}
        </div>
        <If condition={getJobAmountSummarySuccess && !isFetchingJobAmountSummary && Boolean(jobCount)}>
          <div className="mt-2 text-xl font-bold leading-6 text-tertiaryText">
            {`${jobCount} ${jobCount === 1 ? 'job' : 'jobs'}`}
          </div>
          <div
            className="customNormalScroll mt-6 box-border flex max-h-60 
					  w-full flex-col gap-2 overflow-y-auto overflow-x-hidden">
            <Bar options={options} data={chartData} />
          </div>
        </If>
        <If condition={getJobAmountSummarySuccess && !jobCount && !isFetchingJobAmountSummary}>
          <div className="rotateChild flex h-full flex-col items-center justify-center gap-2">
            <LineGraphIcon />
            <div className="text-sbase font-semibold text-accentText">No jobs in this year</div>
            <div
              onClick={() => {
                navigate(routesPath.CREATE_JOB);
              }}
              className="flex cursor-pointer items-center gap-2">
              <div id="create-new-job/jobs-amount-summary" className="text-px13 font-semibold text-secondary">
                Create new job
              </div>
              <ArrowRightIcon />
            </div>
          </div>
        </If>
        <If condition={isFetchingJobAmountSummary || isJobAmountSummaryUninitialized}>
          <div className="m-auto flex h-full items-center justify-center">
            <Lottie className="h-20 w-20" animationData={LoadingSpinnerAnimation} loop={true} />
          </div>
        </If>
        <If condition={isJobAmountSummaryError && !isFetchingJobAmountSummary && !isJobAmountSummaryUninitialized}>
          <GenericError showBackButton={false} />
        </If>
      </div>
    </div>
  );
};

export default JobsAmountSummaryWidget;
